import { FormEvent, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const Login = () => {
  const { user, loading, configured, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ||
    "/admin/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Admin Login · Olivia Flemming";
  }, []);

  if (!loading && user) {
    return <Navigate to={from.startsWith("/admin") ? from : "/admin/dashboard"} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await signIn(email.trim(), password);
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    navigate("/admin/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <p className="text-primary text-xs tracking-[0.35em] uppercase mb-4 font-inter font-light text-center">
          Private portal
        </p>
        <h1 className="font-playfair text-3xl md:text-4xl text-white text-center mb-2">
          Admin login
        </h1>
        <p className="text-white/50 text-sm text-center mb-10 font-inter">
          Olivia Flemming bookings
        </p>

        {!configured && (
          <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90 font-inter">
            Supabase is not configured yet. Add <code className="text-primary">VITE_SUPABASE_URL</code> and{" "}
            <code className="text-primary">VITE_SUPABASE_ANON_KEY</code> (see <code>.env.example</code>).
            No credentials are hardcoded.
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-xs uppercase tracking-widest text-white/50 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl bg-secondary border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-primary"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-xs uppercase tracking-widest text-white/50 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl bg-secondary border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-primary"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              className="rounded-xl border border-red-400/50 bg-red-500/15 px-4 py-3 text-sm text-red-100 font-inter space-y-2"
              role="alert"
              data-testid="admin-login-error"
            >
              <p className="font-medium text-red-200">Sign-in failed</p>
              <p className="text-red-100/95 leading-relaxed whitespace-pre-wrap">{error}</p>
              {(error.toLowerCase().includes("email not confirmed") ||
                error.toLowerCase().includes("not confirmed")) && (
                <p className="text-red-100/70 text-xs leading-relaxed">
                  Fastest fix: Supabase → SQL Editor → paste confirm + admin_profiles from{" "}
                  <code className="text-primary">docs/fix-login-now.md</code>, then retry.
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !configured}
            className="w-full rounded-full bg-primary text-black font-medium py-3.5 text-sm hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/admin/forgot-password"
            className="text-sm text-white/50 hover:text-primary transition-colors font-inter"
          >
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
